import styled from 'styled-components';
import { DX } from '../../../theme/v2';

/**
 * A period, drawn as the run it is.
 *
 * The legacy page gives a period one table row and seven columns of links, which says nothing about
 * the fact that the seven are a chain: funds, then files, then jurisdictions, then extraction,
 * transformation, report, approval, submission — each waiting on the one before it. Here every
 * period is the same strip of stages, and the rule above each stage carries its state, so the strip
 * can be read across without reading a word of it.
 */
export const PeriodList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/* Out of this project and back to the list. The app's own chrome has a breadcrumb, but it is two
   dropdowns up in a black bar and nobody reads it as a way back — the way back belongs at the start
   of the line it leads out of. */
export const BackButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-left: calc(-1 * ${DX.space[2]});
  padding: 0;
  border: none;
  border-radius: ${DX.radius.control};
  background: none;
  color: ${DX.textMuted};
  cursor: pointer;
  transition: background-color ${DX.duration} ${DX.ease};

  &:hover {
    background: ${DX.hover};
    color: ${DX.text};
  }
`;

/* No rule between periods. Each one already announces itself — a dot, a name, the date it was
   created — and a page of three periods was drawing three lines to separate three things that were
   never going to be confused with each other. The space does it. */
export const PeriodBlock = styled.section`
  padding: ${DX.space[5]} ${DX.space[5]} ${DX.space[6]};

  /* A folded period is one line, so it does not need the room an open one does. */
  &[data-open='false'] {
    padding-bottom: ${DX.space[2]};
  }

  &[data-open='false'] header {
    margin-bottom: 0;
  }

  /* The fold itself. A row growing from 0fr to 1fr is the one way to animate to a height nobody can
     know in advance; the clipping child carries no padding of its own, because padding on a
     zero-height row still renders and would leave a sliver of the strip under a closed period. */
  .fold-box {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 200ms ${DX.ease};
  }

  .fold-box[data-open='true'] {
    grid-template-rows: 1fr;
  }

  .fold-inner {
    min-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: opacity 160ms ${DX.ease};
  }

  .fold-box[data-open='true'] .fold-inner {
    opacity: 1;
  }

  /* Movement is a nicety here, not the message. */
  @media (prefers-reduced-motion: reduce) {
    .fold-box,
    .fold-inner {
      transition: none;
    }
  }

  /* The period being worked on now: the only one lit, because a list where everything is lit says
     nothing about where the work is. */
  &[data-live='true'] {
    background: ${DX.surfaceSunken};
  }

  /* Filed periods are history — legible, not loud. */
  &[data-settled='true'] .pipe {
    opacity: 0.72;
  }
`;

/* The whole line folds the period, so the whole line is the control: it takes the cursor, it lights
   under it, and the chevron is a mark on it rather than the target. */
export const PeriodHead = styled.header`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  margin: 0 calc(-1 * ${DX.space[3]}) ${DX.space[3]};
  padding: ${DX.space[2]} ${DX.space[3]};
  border-radius: ${DX.radius.item};
  cursor: pointer;
  transition: background-color ${DX.duration} ${DX.ease};

  &:hover {
    background: ${DX.hover};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 1px solid ${DX.accent};
    outline-offset: -1px;
  }

  /* Pointing right when the period is closed and down when it is open, which is the one convention
     nobody has to be taught. */
  .fold {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 16px;
    color: ${DX.textFaint};
    transition: transform 120ms ${DX.ease};
  }

  &[aria-expanded='true'] .fold {
    transform: rotate(90deg);
  }

  /* Where a folded period ended, in its own words — the same sentence the project list prints. */
  .ended {
    font-size: ${DX.fz.sm};
    color: ${DX.textMuted};
  }

  /* When it is due, and how far off that is. */
  .due {
    display: flex;
    align-items: center;
    gap: ${DX.space[2]};
    white-space: nowrap;
  }

  .due em {
    font-style: normal;
    font-size: ${DX.fz.xs};
    color: ${DX.textFaint};
  }

  .due[data-tone='attention'] em {
    color: ${DX.tone.attention};
  }

  .dot {
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${DX.tone.idle};
  }

  &[data-tone='next'] .dot {
    background: ${DX.accent};
  }

  &[data-tone='attention'] .dot {
    background: ${DX.tone.attention};
  }

  /* The period's name is a name, not a link: the line it sits on folds, and every page it used to
     shortcut to is a click away inside the strip that opens. */
  .name {
    font-size: ${DX.fz.md};
    font-weight: ${DX.fw.strong};
    color: ${DX.text};
  }

  /* The row's own menu sits at the end of its line, where a table would put it. The date before it
     is what pushes everything there — one auto margin, or the free space gets split between them
     and the date ends up in the middle of the line. */
  .menu {
    display: inline-flex;
  }

  .filing {
    display: inline-flex;
    align-items: center;
    padding: 1px ${DX.space[2]};
    border: ${DX.hairline};
    border-radius: ${DX.radius.control};
    font-size: ${DX.fz.caption};
    color: ${DX.textMuted};
  }

  /* The date and the menu share the right-hand end; the free space in the line falls before them. */
  .meta {
    margin-left: auto;
    font-size: ${DX.fz.xs};
    color: ${DX.textMuted};
    white-space: nowrap;
  }

  /* A pause between what the period is and what you can do to it. */
  .run {
    margin-left: ${DX.space[3]};
  }
`;

export const Pipe = styled.div`
  display: flex;
  /* A gap between the stages, so each rule is its own segment and the row reads as eight things
     rather than as one ruled table. It is also what the vertical hairlines were for, which is why
     they are gone. */
  gap: ${DX.space[4]};
`;

/**
 * One stage.
 *
 * Four states, and they are the four the legacy columns already draw: waiting (the step has not
 * come), the verb (what that column renders as a link today — "Add AIFs", "Start Extraction"),
 * running, and done. The rule on top is the same statement in colour.
 */
export const Stage = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  text-align: left;

  /* The state is the rule on top and nothing else: no outline, no fill, no shape. Eight outlined
     cards in a row drew eight boxes and left the values inside them; a line above each stage says
     the same thing with one stroke, and what is left on the page is the values. */
  border-top: 2px solid ${DX.borderSoft};
  transition: border-top-color ${DX.duration} ${DX.ease};

  /* The hairline between two stages stays after all — with the gap it is no longer the thing
     separating them, it is the edge of the column they stand in. */
  & + & {
    border-left: ${DX.hairlineSoft};
  }

  > .face {
    position: relative;
    height: 100%;
    padding: ${DX.space[3]} 0 ${DX.space[2]};
  }

  & + & > .face {
    padding-left: ${DX.space[3]};
  }

  .nm {
    font-size: ${DX.fz.caption};
    line-height: ${DX.lh.caption};
    color: ${DX.textMuted};
    margin-bottom: ${DX.space[1]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* The cell is the periods page's own column, mounted unchanged — so its markup is the legacy
     label: a container, a span for the word, an icon for the arrow. Only the voice changes here.
     The colour comes from the stage's tone rather than from the value, because a column that
     paints "Finished" green and "15 successful" a different green is describing itself, not the
     work. */
  .cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: ${DX.textMuted};
  }

  /* The label sits in a button, and a button's colour is the browser's own — so an inherited colour
     on the word inside it lands on black instead of on the stage's tone. The chain has to pass
     through the button. */
  .cell button {
    color: inherit;
  }

  .cell .action-label-content {
    display: flex;
    /* Values wrap onto a second line; the tick and the dot belong to the first one. */
    align-items: flex-start;
    gap: ${DX.space[2]};
    min-width: 0;
  }

  /* Values wrap rather than truncate: "Pending Approval" cut to "Pending Appro…" tells you less
     than the same words on two lines. */
  .cell span {
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: inherit;
    text-decoration: none;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .cell [role='button']:hover span,
  .cell button:not([aria-disabled='true']):hover span {
    text-decoration: underline;
  }

  /* ── The way in ────────────────────────────────────────────────────────────
     Eight identical arrows down a row said the same thing eight times. The card takes the click
     instead — the label's own button stretches over it — and the arrow waits for the cursor to say
     that the click leaves the page. A card with more than one thing in it keeps its own targets. */
  .cell:has(> button:only-child) > button:not([aria-disabled='true'])::after,
  .cell:has(> div:only-child) > div > button:not([aria-disabled='true'])::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  /* Under the cursor the rule firms up rather than the stage filling in — the fill was the one thing
     on the row that put a block of colour behind text. */
  /* Only where the line is grey to begin with. A stage that is your turn or under way already says
     so in colour, and turning it grey under the cursor took the meaning away instead of adding the
     hover to it. */
  &[data-tone='done']:has(.cell button:not([aria-disabled='true'])):hover,
  &[data-tone='idle']:has(.cell button:not([aria-disabled='true'])):hover,
  &[data-tone='muted']:has(.cell button:not([aria-disabled='true'])):hover,
  &[data-tone='ahead']:has(.cell button:not([aria-disabled='true'])):hover {
    border-top-color: ${DX.textFaint};
  }

  /* And the value it would open is underlined, wherever on the card the cursor happens to be. */
  &:has(.cell button:not([aria-disabled='true'])):hover
    .cell
    > button:not([aria-disabled='true']):first-child
    span,
  &:has(.cell button:not([aria-disabled='true'])):hover
    .cell
    > div:first-child
    > button:not([aria-disabled='true'])
    span {
    text-decoration: underline;
  }

  /* A stage that has not come says nothing.
     The legacy cell falls back to a placeholder, and that placeholder is the name of the step the
     period is standing on — so a fresh period reads "Upload Files" in the five columns that have
     nothing to do with files. The element stays, its text swapped for a dash: the column is empty,
     and pretending otherwise is where the row started looking broken. */
  .cell > button > span:first-child {
    font-size: 0;
    /* The same head slot the values have, so the dashes stand in the same column as the words. */
    padding-left: 19px;
  }

  .cell > button > span:first-child::before {
    content: '—';
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: ${DX.textFaint};
  }

  .cell svg {
    flex: none;
    width: 12px;
    height: 12px;
    color: ${DX.textFaint};
  }

  /* Icons that lead a value go: an upload glyph beside "Upload files" and a clock beside "Pending
     Approval" say what the words already say, and each one shifts its value out of line with the
     rest of the row. */
  .cell .action-label-content > svg:first-child {
    display: none;
  }

  /* No arrows. Each one repeated what the card already offers, and a cell that says two things
     carried two of them; the card lighting under the cursor says the same in one gesture and moves
     nothing. Icons that lead a value stay — they are not arrows. */
  .cell .action-label-content > svg:last-child {
    display: none;
  }

  /* Except the second line of the approval cell, which is not another verb — it is the comments on
     the report, and the legacy cell leads it with a speech bubble. Hiding every leading icon and
     then giving every line the stage's own glyph put a paper plane in front of "1/1 Resolved" and
     made it read as a second thing to send. */
  &[data-stage='approval']
    .cell
    > button:not(:first-child)
    .action-label-content
    > svg:first-child {
    display: inline-flex;
    /* The line it leads is 18px tall and the glyph is 12: three pixels to sit on the middle of the
       words rather than on top of them. And it takes their colour — a grey bubble in front of blue
       text reads as a different thing from the sentence it belongs to. */
    margin-top: 3px;
    color: inherit;
  }

  &[data-stage='approval'] .cell > button:not(:first-child) .action-label-content::before {
    display: none;
  }

  /* ── What state looks like ─────────────────────────────────────────────────
     A tick for a step that is behind us: quiet, and it does not compete with the orange and the red
     that mean something is wrong. Only on the first line — a cell that says two things says the
     second one for a reason. */
  /* One slot at the head of every value in the row, filled or not: a tick where the step is behind
     us, a dot where the value is a state, nothing where there is neither. Without it the values sit
     at three different left edges depending on what happened to them. */
  .cell > button .action-label-content::before {
    content: '';
    flex: none;
    width: 11px;
    margin-right: 2px;
    font-size: 11px;
    line-height: ${DX.lh.sm};
    color: ${DX.textFaint};
  }

  &[data-tone='done'] .cell > button:first-child .action-label-content::before {
    content: '✓';
  }

  /* Your turn gets a mark of its own rather than an empty slot in front of the verb: the same width
     as the tick, so the column holds. */
  &[data-tone='next'] .cell > button .action-label-content::before,
  &[data-tone='comment'] .cell > button .action-label-content::before {
    content: '';
    width: 6px;
    height: 6px;
    margin: 6px 7px 0 0;
    border-radius: 50%;
    background: ${DX.accent};
  }

  /* What did not come through is not written the same way as what did: it wears the chip, with the
     dot in the colour of the trouble. */
  &[data-mixed='true'] .cell > button:not(:first-child) .action-label-content {
    padding: 1px ${DX.space[3]};
    border: ${DX.hairline};
    border-radius: ${DX.radius.control};
    background: ${DX.surface};
  }

  &[data-mixed='true'] .cell > button:not(:first-child) .action-label-content::before {
    content: '';
    width: 6px;
    height: 6px;
    margin: 6px 0 0;
    border-radius: 50%;
    background: ${DX.tone.warning};
  }

  /* A chip is for a value that is in a state, not for a value that is a fact: four funds is a
     number, "1 not extracted" is a condition. The dot carries the colour so the words stay black. */
  &[data-tone='attention'] .cell > button .action-label-content,
  &[data-tone='running'] .cell > button .action-label-content,
  &[data-stage='approval'][data-tone='muted'] .cell > button .action-label-content,
  .cell > div > button .action-label-content {
    padding: 1px ${DX.space[3]};
    border: ${DX.hairline};
    border-radius: ${DX.radius.control};
    background: ${DX.surface};
  }

  &[data-tone='attention'] .cell > button .action-label-content::before,
  &[data-tone='running'] .cell > button .action-label-content::before,
  &[data-stage='approval'][data-tone='muted'] .cell > button .action-label-content::before,
  .cell > div > button .action-label-content::before {
    content: '';
    flex: none;
    width: 6px;
    height: 6px;
    margin-top: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &[data-stage='approval'][data-tone='muted'] .cell > button .action-label-content::before {
    background: ${DX.tone.next};
  }

  /* The one control on the row that changes something rather than opening something: its chevron is
     part of the control, not a way out, so it stays. */
  .cell > div > button .action-label-content > svg:last-child {
    display: inline-flex;
  }

  &[data-stage='submission'] .cell > div > button .action-label-content {
    color: ${DX.text};
  }

  &[data-stage='submission'][data-submitted='false']
    .cell
    > div
    > button
    .action-label-content::before {
    background: ${DX.tone.warning};
  }

  &[data-stage='submission'][data-submitted='true']
    .cell
    > div
    > button
    .action-label-content::before {
    background: ${DX.tone.ok};
  }

  /* The legacy label paints every path inside it green or blue from a prop; this outranks it so an
     arrow follows the text it belongs to. Outline icons declare fill=none on the svg — filling one
     of those turns a clock face into a grey square, and every react-icon carries a stroke attribute
     whether it strokes anything or not, so the fill attribute is what tells them apart. */
  .cell .action-label-content svg:not([fill='none']) path {
    fill: currentColor;
  }

  .cell .action-label-content svg[fill='none'] path {
    fill: none;
    stroke: currentColor;
  }

  .sb {
    font-size: ${DX.fz.caption};
    line-height: ${DX.lh.caption};
    color: ${DX.textFaint};
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Whether anything is happening ─────────────────────────────────────────
     A strip laid over the rule, and a gradient sliding along it: the light travels the width of the
     stage the machine is working on, and only that one — half the steps in this pipeline wait for a
     person, and a row where everything moves would be lying about all of them. */
  /* The light runs on the two stages that are the row's current business: the one the machine is
     working on, and the one that stopped with something in it. Both keep their own colour — the
     sweep is the same movement, the custom property is whose it is. */
  &[data-tone='running'] {
    --lit: ${DX.accent};
    border-top-color: ${DX.accent};
  }

  &[data-tone='attention'] {
    --lit: ${DX.tone.warning};
  }

  &[data-tone='running']::before,
  &[data-tone='attention']::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      var(--lit) 0%,
      var(--lit) 18%,
      ${DX.surface} 38%,
      var(--lit) 58%,
      var(--lit) 100%
    );
    background-size: 260% 100%;
    animation: dx-edge 1.8s linear infinite;
  }

  @keyframes dx-edge {
    from {
      background-position: 180% 0;
    }
    to {
      background-position: -60% 0;
    }
  }

  /* Movement is the message, not decoration: where it is turned off, the rule stays lit. */
  @media (prefers-reduced-motion: reduce) {
    &[data-tone='running']::before,
    &[data-tone='attention']::before {
      display: none;
    }
  }

  /* The stage waiting for a person does not pulse — it takes three quiet breaths when it arrives
     and then holds. Work in hand and your turn are not the same news. */
  &[data-tone='next'],
  &[data-tone='comment'] {
    animation: dx-breathe 2.4s ease-out 3;
  }

  @keyframes dx-breathe {
    0% {
      border-top-color: ${DX.accent};
    }
    40% {
      border-top-color: ${DX.accentSoft};
    }
    100% {
      border-top-color: ${DX.accent};
    }
  }

  /* No fill anywhere: the rule and the value carry the state between them. A stage that has happened
     has a line you can see and a black value; one that has not come is a fainter line with the value
     greyed. */
  &[data-tone='done'] {
    border-top-color: ${DX.tone.idle};
  }
  &[data-tone='done'] .cell {
    color: ${DX.text};
  }

  &[data-tone='idle'],
  &[data-tone='muted'],
  &[data-tone='ahead'] {
    border-top-color: ${DX.borderSoft};
  }

  &[data-tone='muted'] .cell,
  &[data-tone='ahead'] .cell {
    color: ${DX.textMuted};
  }

  &[data-tone='running'] .cell {
    color: ${DX.text};
  }

  /* What the period is standing on — the one stage in the row that asks for a click. Dashed, because
     it has not happened yet. */
  &[data-tone='next'],
  &[data-tone='comment'] {
    border-top-color: ${DX.accent};
    border-top-style: dashed;
  }
  &[data-tone='next'] .cell,
  &[data-tone='comment'] .cell {
    color: ${DX.accent};
  }

  /* Two verbs get a glyph instead of the dot — the ones that ask for a real piece of work rather
     than a click. Drawn here as a mask on the same slot rather than swapped inside the legacy cell,
     which is not ours to edit: one thin line icon, the accent's colour, the tick's width. */
  &[data-stage='files'][data-tone='next'] .cell > button .action-label-content::before,
  &[data-stage='approval'][data-tone='next'] .cell > button .action-label-content::before,
  &[data-stage='submission'][data-tone='next'] .cell > button .action-label-content::before {
    width: 12px;
    height: 12px;
    margin: 3px 1px 0 0;
    border-radius: 0;
    background: ${DX.accent};
    mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
  }

  &[data-stage='files'][data-tone='next'] .cell > button .action-label-content::before {
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'/%3E%3Cpath d='M7 9l5-5 5 5'/%3E%3Cpath d='M12 4v12'/%3E%3C/svg%3E");
  }

  &[data-stage='approval'][data-tone='next'] .cell > button .action-label-content::before,
  &[data-stage='submission'][data-tone='next'] .cell > button .action-label-content::before {
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10 14l11-11'/%3E%3Cpath d='M21 3l-6.5 18a.55.55 0 0 1-1 0L10 14l-7-3.5a.55.55 0 0 1 0-1z'/%3E%3C/svg%3E");
  }

  /* The words stay black and the dot carries the colour — the same rule the rest of the row lives
     by. Orange text made the chip shout what its own dot already says, and "11 successful" is not
     bad news in the first place. */
  &[data-tone='attention'] {
    border-top-color: ${DX.tone.warning};
  }

  &[data-tone='attention'] .cell {
    color: ${DX.text};
  }

  &[data-tone='attention'] .cell > button .action-label-content::before {
    background: ${DX.tone.warning};
  }
`;

/* What is already settled about this period before anyone opens the drawer: which report, whose
   project, who has it. The form this replaces asked all three as fields and then disabled them —
   the questions were never live, so they are stated rather than asked. */
export const Context = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DX.space[2]};
  padding-bottom: ${DX.space[4]};
  border-bottom: ${DX.hairlineSoft};

  > div {
    display: flex;
    align-items: baseline;
    gap: ${DX.space[3]};
    min-width: 0;
  }

  .k {
    flex: 0 0 92px;
    font-size: ${DX.fz.xs};
    color: ${DX.textMuted};
  }

  .v {
    flex: 1;
    min-width: 0;
    font-size: ${DX.fz.sm};
    color: ${DX.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/* A line in the right-hand half of the create drawer: what comes across, and what it amounts to.
   Built to sit in that drawer's CollectedList beside the fund rows it replaces. */
export const CarryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  padding: ${DX.space[3]} ${DX.space[4]} ${DX.space[3]} 18px;
  border-bottom: ${DX.hairlineSoft};
  font-size: ${DX.fz.sm};
  line-height: 20px;

  &:last-child {
    border-bottom: none;
  }

  /* The files are a list of names, not a table of values: ruling every one of them off from the
     next made seven names look like seven facts to check. */
  &[data-plain='true'] {
    padding-top: 3px;
    padding-bottom: 3px;
    border-bottom: none;
    color: ${DX.text7};
  }

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em {
    font-style: normal;
    color: ${DX.textMuted};
    font-size: ${DX.fz.xs};
  }

  b {
    font-weight: ${DX.fw.regular};
    color: ${DX.textMuted};
    font-size: ${DX.fz.xs};
    white-space: nowrap;
  }
`;

/* What the run is doing, under the row it is doing it to. */
export const Live = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[2]};
  padding: 2px ${DX.space[3]};
  border-radius: ${DX.radius.control};
  background: ${DX.accentSoft};
  font-size: ${DX.fz.xs};
  color: ${DX.accent};
  white-space: nowrap;
`;
